import React from 'react';
import { Position } from '../types/game';
import { cn } from '../lib/utils';

interface ConnectFourBoardProps {
  board: (string | null)[][];
  onColumnClick?: (col: number) => void;
  disabled?: boolean;
  availableColumns?: number[];
  lastMove?: Position | null;
  winningCells?: Position[];
}

const ConnectFourBoard: React.FC<ConnectFourBoardProps> = ({
  board,
  onColumnClick,
  disabled = false,
  availableColumns = [],
  lastMove,
  winningCells = []
}) => {
  const handleCellClick = (col: number) => {
    if (!disabled && onColumnClick && availableColumns.includes(col)) {
      onColumnClick(col);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center w-full h-full p-2 sm:p-4">
      {/* 棋盘主体 */}
      <div className="max-w-[700px] max-h-[700px] bg-blue-100 rounded-xl p-3 sm:p-4 shadow-inner w-full">
        {/* 棋盘背景 */}
        <div className="bg-blue-200 rounded-xl p-3 sm:p-4">
          <div className="grid grid-cols-8 gap-1.5 sm:gap-2 aspect-square w-full">
            {board.map((row, rowIndex) => {
              return row.map((cell, colIndex) => {
                const isLastMove = lastMove?.row === rowIndex && lastMove?.col === colIndex;
                const isWinningCell = winningCells.some(
                  pos => pos.row === rowIndex && pos.col === colIndex
                );
                const isAvailable = availableColumns.includes(colIndex);

                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={cn(
                      "relative w-full h-full flex items-center justify-center rounded-lg",
                      "transition-all duration-200 aspect-square",
                      "border-2 border-blue-500 bg-blue-100",
                      !disabled && isAvailable && "cursor-pointer hover:bg-green-200",
                      disabled && "cursor-not-allowed opacity-75",
                      isLastMove && "ring-2 ring-yellow-400 ring-offset-2 ring-offset-blue-200",
                      isWinningCell && "ring-3 ring-green-400 ring-offset-2 ring-offset-blue-200 animate-pulse"
                    )}
                    onClick={() => handleCellClick(colIndex)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !disabled && isAvailable) {
                        e.preventDefault();
                        handleCellClick(colIndex);
                      }
                    }}
                  >
                    {/* 棋子 */}
                    {cell && (
                      <div
                        className={cn(
                          "w-5/6 h-5/6 sm:w-4/5 sm:h-4/5 rounded-full shadow-lg",
                          cell === 'black'
                            ? "bg-gradient-to-br from-gray-800 to-black border-2 border-gray-700"
                            : "bg-gradient-to-br from-white to-gray-100 border-2 border-gray-300",
                          isLastMove && "ring-4 ring-yellow-300 ring-offset-2"
                        )}
                      >
                        {cell === 'black' && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-gray-600 to-gray-800 rounded-full" />
                          </div>
                        )}
                        {cell === 'white' && (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-gray-200 to-white rounded-full" />
                          </div>
                        )}
                        {isLastMove && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                            <span className="text-xs">⭐</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 空位置指示器 */}
                    {!cell && isAvailable && !disabled && (
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-400 rounded-full opacity-70 animate-pulse" />
                    )}
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>

      {/* 列指示器 */}
      <div className="max-w-[700px] pl-8 pr-8 flex gap-1.5 sm:gap-2 mt-3 sm:mt-4 w-full">
        {Array.from({ length: 8 }).map((_, colIndex) => {
          const isAvailable = availableColumns.includes(colIndex);

          return (
            <button
              key={colIndex}
              className={cn(
                "flex-1 h-8 sm:h-10 rounded-lg transition-all duration-200",
                "flex items-center justify-center font-semibold",
                isAvailable
                  ? "bg-green-400 hover:bg-green-500 cursor-pointer text-white shadow-md active:scale-95"
                  : "bg-gray-300 cursor-not-allowed text-gray-500"
              )}
              onClick={() => handleCellClick(colIndex)}
              disabled={disabled}
            >
              <span className="text-sm sm:text-base">{colIndex + 1}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectFourBoard;